import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X } from "lucide-react";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { AcquirePlusDialog } from "./AcquirePlusDialog";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { posthog } from "posthog-js";

const DISMISS_KEY = "plusUpsellBannerDismissed";

/**
 * Six marketing copy variants for the Plus upsell banner.
 * Rotated randomly on each component mount.
 */
export const BANNER_COPIES: readonly string[] = [
  "Conoce los beneficios de",
] as const;

/**
 * Three-state visibility machine for the banner lifecycle.
 */
type VisibilityState = "visible" | "exiting" | "dismissed";

/**
 * Thin dismissible banner on `/store` inviting logged-in non-Plus users
 * to subscribe to Carriapp Plus.
 *
 * Visibility gate:
 * - Hidden when user is not logged in
 * - Hidden when user is already a Plus subscriber
 * - Hidden when dismiss has been persisted via localStorage (banner variant only)
 *
 * 3-state visibility machine (banner variant only):
 * - 'visible': Banner is shown with entry animation (slide-up + fade-in)
 * - 'exiting': Banner is animating out (slide-down + fade-out)
 * - 'dismissed': Banner is unmounted (final state)
 *
 * Dismiss writes `localStorage` key `plusUpsellBannerDismissed = "true"`.
 * CTA click opens {@link AcquirePlusDialog} via controlled state.
 *
 * @param variant - `"banner"` (default, full-width fixed with effects) or `"sidebar"` (inline, no effects).
 * @returns JSX.Element when visible, null otherwise.
 */
export const PlusUpsellBanner = ({ variant = "banner" }: { variant?: "banner" | "sidebar" }): JSX.Element | null => {
  const { isLogged, isPremiumUser } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<VisibilityState>(() =>
    localStorage.getItem(DISMISS_KEY) === "true" ? "dismissed" : "visible",
  );
  const [copy] = useState<string>(() =>
    BANNER_COPIES[Math.floor(Math.random() * BANNER_COPIES.length)],
  );
  const [entered, setEntered] = useState(false);
  const isDesktop = useMediaQuery(isDesktopSize);

  // Ref to store transition timeout fallback
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sendEventPH = () => {
      posthog.capture('Ver Plus');
  }
  /**
   * Triggers the entrance animation after the initial render.
   * Double requestAnimationFrame ensures the browser paints the
   * off-screen position (translate-y-full) before transitioning in.
   */
  useEffect(() => {
    if (visibility !== "visible") return;
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        setEntered(true);
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [visibility]);

  // --- Visibility gate ---
  if (!isLogged || isPremiumUser) {
    return null;
  }
  if (variant === "banner" && visibility === "dismissed") {
    return null;
  }

  /**
   * Handles the transition end event to complete the exit animation.
   * Only triggers when the banner is in 'exiting' state.
   */
  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>): void => {
    // Only handle the wrapper div transition
    if (e.currentTarget !== e.target) return;
    if (visibility === "exiting") {
      // Clear any fallback timeout
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      setVisibility("dismissed");
    }
  };

  /**
   * Persists dismiss state to localStorage and triggers exit animation.
   * Does not immediately unmount — waits for CSS transition to complete.
   */
  const handleDismiss = (): void => {
    // Early exit if already exiting or dismissed
    if (visibility !== "visible") return;

    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // localStorage unavailable — in-memory dismiss only
    }

    // Set exiting state to trigger CSS transition
    setVisibility("exiting");

    // Fallback timeout in case onTransitionEnd doesn't fire (browser quirk)
    // 600ms provides buffer beyond the 500ms transition duration
    transitionTimeoutRef.current = setTimeout(() => {
      setVisibility("dismissed");
    }, 600);
  };

  // Determine classes based on visibility and entrance state
  const getWrapperClasses = (): string => {
    const baseClasses = "fixed bottom-0 left-0 right-0 z-40 transition-all duration-500 ease-out";

    if (visibility === "visible") {
      return entered
        ? `${baseClasses} translate-y-0 opacity-100`
        : `${baseClasses} translate-y-full opacity-0`;
    }

    // exiting state
    return `${baseClasses} translate-y-full opacity-0`;
  };

  // --- Sidebar variant: inline, no effects, no dismiss ---
  if (variant === "sidebar") {
    return (
      <div data-testid="plus-upsell-banner-sidebar" className="w-full">
        <Card className="border-primary/30 bg-background py-2 shadow-none gap-2">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex h-4 shrink-0 items-center rounded-full border border-border/50 bg-background px-1.5 text-[15px] font-semibold leading-none">
                <span className="bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-clip-text text-transparent">
                  +Plus
                </span>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col text-sm font-semibold gap-2">
            <span>Ahorra sin límites en cada compra</span>
            <Button
              onClick={() => { sendEventPH(); setIsDialogOpen(true); }}
              size="sm"
              type="button"
              variant="default"
              className="shrink-0 text-xs h-7 px-3 font-semibold"
            >
              Ver más
            </Button>

          </CardContent>
          <div className="flex items-center justify-between gap-2">

          </div>
        </Card>
        <AcquirePlusDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      </div>
    );
  }

  return (
    <div
      data-testid="plus-upsell-banner"
      className={getWrapperClasses()}
      onTransitionEnd={handleTransitionEnd}
    >
      <Card className="overflow-hidden border-primary bg-background max-w-2xl mx-auto py-0 text-foreground shadow-[0_18px_45px_-25px_hsl(var(--primary)/0.18)] gap-0">
        <CardHeader className="flex p-0 w-full justify-end">
          <Button
            aria-label="Cerrar banner de Carriapp Plus"
            onClick={handleDismiss}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </CardHeader>

        <CardContent className="flex items-center gap-3 px-4 pt-0 pb-6">
          {isDesktop ? (
            <div className="flex w-full items-center justify-between gap-0 flex-wrap">
              <div className="flex-1 min-w-0">
                <span
                  data-testid="plus-upsell-banner-copy"
                  className="text-base text-foreground/80 font-semibold"
                >
                  {copy}{" "}
                </span>
                <span
                  className={`font-bold russo-one-regular logo-text text-sm ml-2 text-foreground`}
                >
                  Carriapp
                </span>
                <span className="inline-flex h-4 ml-2 items-center rounded-full border border-border/50 bg-background px-1.5 text-[15px] font-semibold leading-none">
                  <span className="font-semibold bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-[length:220%_220%] bg-clip-text text-transparent animate-[ai-border-flow_8s_cubic-bezier(0.4,0,0.2,1)_infinite]">
                    +Plus
                  </span>
                </span>
              </div>
              <Button
                onClick={() => {sendEventPH();setIsDialogOpen(true)}}
                size="lg"
                type="button"
                variant="default"
                className="flex-shrink-0 font-semibold text-base px-6"
              >
                Quiero Carriapp +Plus
              </Button>
            </div>
          ) : (
            <div className="flex flex-col w-full items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  data-testid="plus-upsell-banner-copy"
                  className="text-base text-foreground/80 font-semibold"
                >
                  {copy}{" "}
                </span>
              </div>
              <div className="flex gap-0">
                <span
                  className={`font-bold russo-one-regular logo-text text-sm text-foreground`}
                >
                  Carriapp
                </span>
                <span className="inline-flex h-4 items-center rounded-full border border-border/50 bg-background px-1.5 text-[15px] font-semibold leading-none"></span>
                <span className="font-semibold bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-[length:220%_220%] bg-clip-text text-transparent animate-[ai-border-flow_8s_cubic-bezier(0.4,0,0.2,1)_infinite]">
                  +Plus
                </span>
              </div>
              <Button
                onClick={() => {sendEventPH();setIsDialogOpen(true)}}
                size="lg"
                type="button"
                variant="default"
                className="font-semibold w-full text-base"
              >
                Quiero Carriapp +Plus
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AcquirePlusDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
};
